import {
	type FileSelectionErrorReason,
	fileSelectionErrorReasons,
} from "@/components/AddData/fileSelectionResolver";
import type {
	GroupwareAcceptResolution,
	GroupwareSelectedItem,
} from "@/components/AddData/groupwareSelection.types";
import { isTiffFileName } from "@/components/AddData/uploadTypeResolver";

/** DTM / オルソの組を成す tiff の件数（GI-2） */
export const GROUPWARE_TIFF_PAIR_SIZE = 2;

/**
 * 積み上げの重複判定と v-for の key に使う識別子。
 * ノード ID はバケットをまたいで一意とは限らないため、バケット ID と組で使う（GI-36）
 */
export const groupwareItemKey = (item: GroupwareSelectedItem): string =>
	`${item.bucketId}/${item.id}`;

/** groupwareItemKey で重複を除く（先に積んだものを優先し、順序を保つ） */
const dedupe = (
	items: readonly GroupwareSelectedItem[],
): GroupwareSelectedItem[] => {
	const seen = new Set<string>();
	const result: GroupwareSelectedItem[] = [];
	for (const item of items) {
		const key = groupwareItemKey(item);
		if (seen.has(key)) continue;
		seen.add(key);
		result.push(item);
	}
	return result;
};

const invalid = (
	reason: FileSelectionErrorReason,
): GroupwareAcceptResolution => ({
	kind: "invalid",
	reason,
});

/**
 * ピッカーの「次へ」で受け取った選択と、積み上げ済みの tiff から次の動きを決める（GI-3 / GI-15 / GI-17 / GI-30）。
 * - tiff を含まず積み上げ中でもない -> 1 件なら passThrough（既存の単一ファイル経路）、複数なら invalid（multipleNonTiff）
 * - tiff を含まないが積み上げ中 / tiff と他形式の混在 -> invalid（tiffCountMismatch）
 * - すべて tiff -> 積み上げと重複排除した結果が 1 件なら accumulate、2 件なら tiffPair、それ以上は invalid
 *
 * @param accepted ピッカーが返した選択（ページ内の選択順）
 * @param picked これまでに積み上げた tiff（0〜2 件）
 */
export const resolveGroupwareAccept = (
	accepted: readonly GroupwareSelectedItem[],
	picked: readonly GroupwareSelectedItem[],
): GroupwareAcceptResolution => {
	const tiffCount = accepted.filter((item) => isTiffFileName(item.name)).length;
	if (tiffCount === 0) {
		if (picked.length > 0) {
			return invalid(fileSelectionErrorReasons.tiffCountMismatch);
		}
		// 既存経路は単一ファイルしか扱えない。判定はここに集約し、呼び出し側では再確認しない
		return accepted.length > 1
			? invalid(fileSelectionErrorReasons.multipleNonTiff)
			: { kind: "passThrough" };
	}
	if (tiffCount !== accepted.length) {
		return invalid(fileSelectionErrorReasons.tiffCountMismatch);
	}

	const merged = dedupe([...picked, ...accepted]);
	if (merged.length === 1) {
		return { kind: "accumulate", picked: [merged[0]] };
	}
	if (merged.length === GROUPWARE_TIFF_PAIR_SIZE) {
		return { kind: "tiffPair", picked: [merged[0], merged[1]] };
	}
	return invalid(fileSelectionErrorReasons.tiffCountMismatch);
};
